// Cấu hình mốc phạt SLA tự động (leo thang theo lần). Danh mục mốc (code, tên, mô tả, áp dụng) là CỐ ĐỊNH
// (logic anchor→target + trạng thái nằm ở cron order service); người dùng chỉ cấu hình: giờ + bộ phận + điểm
// cho từng lần. Giữ code này ĐỒNG BỘ với catalog phía order service (cron).

export const SLA_MAX_LEVEL = 3;

// Tách HOÀN TOÀN 2 bộ mốc: đơn thường (NORMAL) và đơn kiểm đếm (COUNTING) — độc lập để chính sách 2 loại
// đơn có thể chỉnh khác nhau về sau mà không đụng nhau.
export type SlaAppliesTo = "NORMAL" | "COUNTING";

export interface SlaMilestoneMeta {
  code: string;
  name: string;
  from: string; // mốc bắt đầu đếm (hiển thị)
  to: string; // mốc phải đạt (hiển thị)
  appliesTo: SlaAppliesTo; // NORMAL (đơn thường) | COUNTING (đơn kiểm đếm)
  defaultHours: number;
}

// Thứ tự = thứ tự vòng đời đơn, nhóm theo loại đơn. Mỗi mốc = việc của ĐÚNG MỘT vai (không chồng lấn):
// ranh giới giữa 2 bước là thao tác của bên giao, thời gian chờ bên nhận tới nhận tính cho bên nhận.
// Bước đầu tính từ lúc XÁC NHẬN đơn (giai đoạn chờ xác nhận đã có phiếu "Quá hạn xác nhận" riêng).
export const SLA_MILESTONES: SlaMilestoneMeta[] = [
  // --- Đơn thường (3 mốc) ---
  { code: "N1_PICKUP", name: "Chưa đưa hàng dơ về cửa hàng (tài xế)", from: "Từ lúc tài xế xác nhận đơn", to: "Hàng dơ về cửa hàng", appliesTo: "NORMAL", defaultHours: 8 },
  { code: "N2_WASH", name: "Chưa hoàn thành giặt (NV giặt)", from: "Từ lúc hàng về cửa hàng (phân loại, kiểm đếm)", to: "Sẵn sàng giao", appliesTo: "NORMAL", defaultHours: 8 },
  { code: "N3_COMPLETE", name: "Chưa giao hàng / hoàn thành đơn (tài xế)", from: "Từ lúc sẵn sàng giao", to: "Hoàn thành đơn", appliesTo: "NORMAL", defaultHours: 12 },
  // --- Đơn kiểm đếm (5 mốc) ---
  { code: "C1_COUNTER", name: "Chưa kiểm đếm / bàn giao tài xế (NV kiểm đếm)", from: "Từ lúc NV kiểm đếm xác nhận đơn", to: "Bấm Giao tài xế", appliesTo: "COUNTING", defaultHours: 2 },
  { code: "C2_PICKUP", name: "Chưa đưa hàng dơ về cửa hàng (tài xế)", from: "Từ lúc chờ giao tài xế", to: "Hàng dơ về cửa hàng", appliesTo: "COUNTING", defaultHours: 4 },
  { code: "C3_WASH", name: "Chưa hoàn thành giặt (NV giặt)", from: "Từ lúc hàng về cửa hàng (phân loại, kiểm đếm)", to: "Sẵn sàng giao", appliesTo: "COUNTING", defaultHours: 8 },
  { code: "C4_DELIVERY", name: "Chưa giao hàng sạch (tài xế)", from: "Từ lúc sẵn sàng giao", to: "Bàn giao cho kiểm đếm", appliesTo: "COUNTING", defaultHours: 12 },
  { code: "C5_COMPLETE", name: "Chưa kiểm đếm hàng sạch / hoàn thành đơn (NV kiểm đếm)", from: "Từ lúc bàn giao cho kiểm đếm", to: "Hoàn thành đơn", appliesTo: "COUNTING", defaultHours: 4 },
];

export const SLA_APPLIES_LABEL: Record<SlaAppliesTo, string> = {
  NORMAL: "Đơn thường",
  COUNTING: "Đơn kiểm đếm",
};

/** Danh mục mốc theo loại đơn (giữ nguyên thứ tự) — dùng để nhóm UI. */
export const SLA_MILESTONES_BY_APPLY: { applies: SlaAppliesTo; milestones: SlaMilestoneMeta[] }[] = [
  { applies: "NORMAL", milestones: SLA_MILESTONES.filter((m) => m.appliesTo === "NORMAL") },
  { applies: "COUNTING", milestones: SLA_MILESTONES.filter((m) => m.appliesTo === "COUNTING") },
];

export interface SlaLevelConfig {
  department_ids: number[];
  points: number;
}

export interface SlaMilestoneConfig {
  hours: number;
  levels: SlaLevelConfig[]; // tối đa SLA_MAX_LEVEL phần tử (Lần 1..3)
}

export interface FaultSlaConfig {
  escalation_interval_hours: number; // khoảng cách giữa các lần (giờ)
  milestones: Record<string, SlaMilestoneConfig>;
}

function emptyLevels(): SlaLevelConfig[] {
  return Array.from({ length: SLA_MAX_LEVEL }, () => ({ department_ids: [], points: 0 }));
}

/** Config rỗng mặc định (mỗi mốc dùng defaultHours, chưa gán bộ phận/điểm). */
export function defaultSlaConfig(): FaultSlaConfig {
  const milestones: Record<string, SlaMilestoneConfig> = {};
  for (const m of SLA_MILESTONES) {
    milestones[m.code] = { hours: m.defaultHours, levels: emptyLevels() };
  }
  return { escalation_interval_hours: 2, milestones };
}

/** Parse chuỗi JSON từ setting → config đầy đủ (bù mốc/lần thiếu để UI luôn hiển thị đủ). */
export function parseSlaConfig(raw: string | undefined | null): FaultSlaConfig {
  const base = defaultSlaConfig();
  if (!raw) return base;
  try {
    const obj = JSON.parse(raw) as Partial<FaultSlaConfig>;
    if (obj?.escalation_interval_hours != null) {
      base.escalation_interval_hours = Number(obj.escalation_interval_hours) || base.escalation_interval_hours;
    }
    const ms = obj?.milestones || {};
    for (const m of SLA_MILESTONES) {
      const saved = (ms as Record<string, SlaMilestoneConfig>)[m.code];
      if (!saved) continue;
      const levels = emptyLevels();
      (saved.levels || []).slice(0, SLA_MAX_LEVEL).forEach((lv, i) => {
        levels[i] = {
          department_ids: Array.isArray(lv?.department_ids) ? lv.department_ids.map(Number).filter(Boolean) : [],
          points: Number(lv?.points) || 0,
        };
      });
      base.milestones[m.code] = { hours: Number(saved.hours) || m.defaultHours, levels };
    }
  } catch {
    /* raw hỏng → dùng default */
  }
  return base;
}

/** Chuẩn hoá config → chuỗi JSON để lưu setting (bỏ level trống để gọn). */
export function serializeSlaConfig(cfg: FaultSlaConfig): string {
  const milestones: Record<string, SlaMilestoneConfig> = {};
  for (const m of SLA_MILESTONES) {
    const src = cfg.milestones[m.code];
    if (!src) continue;
    const levels = (src.levels || [])
      .map((lv) => ({ department_ids: (lv.department_ids || []).filter(Boolean), points: Number(lv.points) || 0 }))
      // Giữ cả level trống ở giữa để không lệch index Lần; chỉ cắt đuôi rỗng.
      ;
    // Cắt các level rỗng ở CUỐI (không bộ phận & 0 điểm).
    while (levels.length > 0) {
      const last = levels[levels.length - 1];
      if (last.department_ids.length === 0 && last.points === 0) levels.pop();
      else break;
    }
    milestones[m.code] = { hours: Number(src.hours) || m.defaultHours, levels };
  }
  return JSON.stringify({
    escalation_interval_hours: Number(cfg.escalation_interval_hours) || 2,
    milestones,
  });
}
